import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { manipulateAsync, SaveFormat } from "expo-image-manipulator";
import { Platform } from "react-native";
import type { CropRegion } from "@/src/components/crop/WebCropOverlay";

type MediaTypeOption = "all" | "images" | "videos";

// Maximum edge (width or height) in pixels for uploaded images.
// Images with a longer edge than this are downscaled before the size guard,
// preserving aspect ratio. 1920 (Full HD long edge) keeps social-media files small
// while retaining enough resolution for the feed.
const MAX_IMAGE_EDGE_PX = 1920;

/**
 * Downscale an image asset so its longest edge is at most MAX_IMAGE_EDGE_PX.
 * - Videos are returned unchanged.
 * - Images already within the limit are returned unchanged (not upscaled).
 * - Falls back to the original asset on manipulation failure.
 *
 * CR-B2-05 note: expo-image-picker's `allowsEditing` is incompatible with
 * `allowsMultipleSelection`, so multi-select post attachments cannot receive a
 * crop step via the built-in picker UI. A custom per-image crop via
 * ImageManipulator is deferred — see deferred-items in the M03/M09 artifact.
 * Single-select avatar/cover crop lives in M09.
 */
export const downscaleImageAsset = async (
  asset: ImagePicker.ImagePickerAsset
): Promise<ImagePicker.ImagePickerAsset> => {
  // Leave videos unchanged
  if (asset.mimeType?.startsWith("video/") || asset.type === "video") {
    return asset;
  }

  const { width, height } = asset;
  if (!width || !height) return asset;

  const longestEdge = Math.max(width, height);
  if (longestEdge <= MAX_IMAGE_EDGE_PX) return asset; // already small enough

  const scale = MAX_IMAGE_EDGE_PX / longestEdge;
  const newWidth = Math.round(width * scale);
  const newHeight = Math.round(height * scale);

  try {
    const result = await manipulateAsync(
      asset.uri,
      [{ resize: { width: newWidth, height: newHeight } }],
      { compress: 0.85, format: SaveFormat.JPEG }
    );
    return {
      ...asset,
      uri: result.uri,
      width: newWidth,
      height: newHeight,
      mimeType: "image/jpeg",
      // fileSize is unknown post-manipulation; omit so the size guard re-evaluates
      fileSize: undefined,
    };
  } catch {
    return asset; // fall back to original on failure
  }
};

/**
 * `cropImages: true` runs each picked IMAGE through the crop step before the
 * downscale and size guard (crop → downscale → size guard, per FIX-03).
 *
 * Multi-select is preserved: each image is cropped in turn. Videos pass through
 * untouched — there is no video cropper. Cancelling the crop for one image drops
 * only that image and keeps the rest of the selection.
 *
 * On native this is a no-op: `allowsEditing` cannot be combined with
 * `allowsMultipleSelection`, and the web overlay depends on react-dom. Native is
 * not a shipping target today (see `pickSingleWithCrop` for the single-select
 * native path, which does get the built-in editor).
 */
export const pickMedia = async (
  fromCamera = false,
  mediaType: MediaTypeOption = "all",
  maxFileSizeMB: number = 10,
  cropImages = false
) => {
  try {
    let allowedMediaTypes: ImagePicker.MediaType | ImagePicker.MediaType[];
    switch (mediaType) {
      case "images":
        allowedMediaTypes = "images";
        break;
      case "videos":
        allowedMediaTypes = "videos";
        break;
      default:
        allowedMediaTypes = ["images", "videos"];
    }

    const permission = fromCamera
      ? await ImagePicker.requestCameraPermissionsAsync()
      : await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (permission.status !== "granted") {
      alert("Permission is required to select media.");
      return null;
    }

    const result = fromCamera
      ? await ImagePicker.launchCameraAsync({
          mediaTypes: allowedMediaTypes,
          quality: 0.7,
        })
      : await ImagePicker.launchImageLibraryAsync({
          mediaTypes: allowedMediaTypes,
          allowsMultipleSelection: true,
          quality: 0.7,
        });

    if (result.canceled) return null;

    // Crop step (FIX-03) — BEFORE downscale, so crop → downscale → size guard.
    // Sequential on purpose: the overlay is modal, so two at once would fight
    // over the same portal container.
    let pickedAssets = result.assets;
    if (cropImages && Platform.OS === "web") {
      const cropped: ImagePicker.ImagePickerAsset[] = [];
      for (const asset of pickedAssets) {
        if (asset.type === "video") {
          cropped.push(asset); // no video cropper — pass through
          continue;
        }
        const result = await showWebCropForAsset(asset);
        if (result === null) continue; // cancelled: drop this one, keep the rest
        cropped.push(result);
      }
      if (cropped.length === 0) return null;
      pickedAssets = cropped;
    }

    // Downscale images before the size guard (CR-B2-04)
    const processedAssets = await Promise.all(
      pickedAssets.map(downscaleImageAsset)
    );

    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    const validAssets = processedAssets.filter((asset) => {
      if (!asset.fileSize) return true; // size unknown after downscale → allow
      if (asset.fileSize > maxSizeBytes) {
        alert(
          `File "${asset.fileName || "selected"}" exceeds maximum size of ${maxFileSizeMB}MB.`
        );
        return false;
      }
      return true;
    });

    if (validAssets.length === 0) return null;
    return validAssets;
  } catch (error) {
    console.error("pickMedia error:", error);
    return null;
  }
};

/**
 * FIX-03: Show an interactive crop UI on web using an imperative React DOM portal.
 *
 * On native, `allowsEditing: true` in ImagePicker provides the built-in crop.
 * On web, `allowsEditing` is silently ignored (no web implementation).
 * This function opens a CSS/DOM crop overlay so that the `pickSingleWithCrop`
 * API stays the same for all callers (including edit-profile.tsx in M09).
 *
 * Requires: `react-dom` (already a dependency), `WebCropOverlay` component.
 * The crop happens BEFORE downscale/size guard (FIX-03 AC-F3-07).
 *
 * Returns the cropped asset, or null if the user cancels (AC-F3-05).
 */
export const showWebCropForAsset = async (
  asset: ImagePicker.ImagePickerAsset
): Promise<ImagePicker.ImagePickerAsset | null> => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    // Dynamic requires to avoid bundling react-dom / the overlay on native.
    /* eslint-disable @typescript-eslint/no-require-imports */
    const ReactModule = require('react');
    const { createRoot } = require('react-dom/client');
    const WebCropOverlay = require('@/src/components/crop/WebCropOverlay').default;
    /* eslint-enable @typescript-eslint/no-require-imports */

    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      if (document.body.contains(container)) document.body.removeChild(container);
    };

    const handleConfirm = async (region: CropRegion) => {
      cleanup();
      try {
        const result = await manipulateAsync(
          asset.uri,
          [{ crop: { originX: region.originX, originY: region.originY, width: region.width, height: region.height } }],
          { compress: 0.85, format: SaveFormat.JPEG }
        );
        resolve({
          ...asset,
          uri: result.uri,
          width: region.width,
          height: region.height,
          mimeType: 'image/jpeg',
          // fileSize unknown after crop; size guard will re-evaluate
          fileSize: undefined,
        });
      } catch {
        // Fall back to original on manipulation failure
        resolve(asset);
      }
    };

    const handleCancel = () => {
      cleanup();
      resolve(null); // AC-F3-05 — cancelling this image leaves others untouched
    };

    root.render(
      ReactModule.createElement(WebCropOverlay, {
        imageUri: asset.uri,
        naturalWidth: asset.width ?? 800,
        naturalHeight: asset.height ?? 600,
        onConfirm: handleConfirm,
        onCancel: handleCancel,
      })
    );
  });
};

/**
 * Picks a single image with a crop step (FIX-03).
 *
 * - Native: uses expo-image-picker's `allowsEditing: true` (built-in crop).
 * - Web: `allowsEditing` is silently ignored, so the picker returns without
 *   cropping; then `showWebCropForAsset` opens an interactive overlay.
 *
 * Crop runs BEFORE downscale and the size guard: crop → downscale → size guard.
 *
 * `allowsMultipleSelection` is intentionally absent (incompatible with
 * `allowsEditing`). Use pickMedia() for multi-select post attachments.
 *
 * Returns the cropped, downscaled asset or null if cancelled/rejected.
 */
export const pickSingleWithCrop = async (
  maxFileSizeMB: number = 10,
): Promise<ImagePicker.ImagePickerAsset | null> => {
  try {
    const permission =
      await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (permission.status !== "granted") {
      alert("Permission is required to select media.");
      return null;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: "images",
      // allowsEditing is honored on native only; web gets the interactive
      // overlay via showWebCropForAsset below.
      allowsEditing: Platform.OS !== 'web',
      quality: 0.7,
    });

    if (result.canceled || result.assets.length === 0) return null;

    let assetToCrop = result.assets[0];

    // Web crop step (FIX-03): open the interactive overlay BEFORE downscale/size guard
    if (Platform.OS === 'web') {
      const cropped = await showWebCropForAsset(assetToCrop);
      if (cropped === null) return null; // user cancelled
      assetToCrop = cropped;
    }

    const resized = await downscaleImageAsset(assetToCrop);

    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    if (resized.fileSize && resized.fileSize > maxSizeBytes) {
      alert(`Selected image exceeds maximum size of ${maxFileSizeMB}MB.`);
      return null;
    }

    return resized;
  } catch (error) {
    console.error("pickSingleWithCrop error:", error);
    return null;
  }
};

export const pickFile = async (
  multiple = false,
  maxFileSizeMB: number = 10 // Default 10MB
) => {
  try {
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "text/plain",
        "text/csv",
        "application/zip",
        "application/x-rar-compressed",
      ],
      copyToCacheDirectory: true,
      multiple, // Now properly using the multiple param
    });

    if (result.canceled) return null;

    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;

    // DocumentPicker can return single object or array when multiple=true
    const assets = Array.isArray(result.assets) ? result.assets : [result];

    const validAssets = assets.filter((file) => {
      if (!file.size) return true; // size is optional
      if (file.size > maxSizeBytes) {
        alert(`File "${file.name}" exceeds maximum size of ${maxFileSizeMB}MB.`);
        return false;
      }
      return true;
    });

    if (validAssets.length === 0) return null;

    // Return same format as original
    return multiple ? { ...result, assets: validAssets } : validAssets[0];
  } catch (error) {
    console.error("pickFile error:", error);
    return null;
  }
};
